import React, { useState, useContext, useEffect, ChangeEvent } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { GlobalContext } from '../../providers/GlobalProvider';
import CustomModuleHeaderLayout from 'components/Common/Layouts/CustomModuleHeaderLayout';
import { buildCaseQuery } from 'utils/queryHelper';
import MonitorTicketTable from 'components/Monitor/Table/MonitorTicketTable';
import SearchFilterComponent from 'components/Monitor/SearchFilterComponent';
import AuditCardDash from 'components/Monitor/MonitorCardDash';
import { useSnackbar } from 'notistack';
import CasesAPI from 'api/CasesAPI';
import ContentError from 'components/Common/ContentError';
import { Typography } from '@mui/material';
import { Player } from '@lottiefiles/react-lottie-player';
import SubscribeModal from 'components/SubscribeModal';
import { Modal } from '@mui/material';
import AddPersonModal from 'components/Monitor/Modals/AddPersonModal';
import UsersAPI from 'api/UsersAPI';
import PaymentsAPI from 'api/PaymentsAPI';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';

const Monitor: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [ autoTab, setAutoTab ] = useState<string>('All');
  const [ cardTab, setCardTab ] = useState<string>('Card');
  const [ pageNum, setPageNum ] = useState(1);
  const [ pageFavNum, setPageFavNum ] = useState(1);
  const [ pageSharedNum, setPageSharedNum ] = useState(1);
  const [ searchVal, setSearchVal ] = useState('');
  const [ trigger, setTrigger ] = useState(false);
  const [ caseQuery, setCaseQuery ] = useState<any>({});
  const [ caseCount, setCaseCount ] = useState<number>(0);
  const [ sharedCaseCount, setSharedCaseCount ] = useState<number>(0);
  const [ favCaseCount, setFavCaseCount ] = useState<number>(0);
  const [ userHasMonitor, setUserHasMonitor ] = useState(false);
  const [ shareModal, setShareModal ] = useState(false);
  const [ selectedCase, setSelectedCase ] = useState<any>({});
  const [ screeningCus, setScreeningCus ] = useState({ isLoading: true, data: '' });
  const [ ownerEmail, setOwnerEmail ] = useState('');
  const [ matchedTeamWithStripe, setMatchedTeamWithStripe ] =
    useState<string>('');
  const [ activeSub, setActiveSub ] = useState(false);
  const [ cases, setCases ] = useState<{
    isLoaded: boolean;
    data: any[];
    error: string | undefined;
  }>({ isLoaded: false, data: [], error: undefined });
  const [ cur_case, setCase ] = useState<{
    isLoaded: boolean;
    error: string | undefined;
    data: any;
  }>({ isLoaded: false, error: undefined, data: {} });
  const [ favCases, setFavCases ] = useState<{
    isLoaded: boolean;
    data: any[];
    error: string | undefined;
  }>({ isLoaded: false, data: [], error: undefined });
  const [ sharedCases, setSharedCases ] = useState<{
    isLoaded: boolean;
    data: any[];
    error: string | undefined;
  }>({ isLoaded: false, data: [], error: undefined });

  const [ selectedOptsType, setSelectedOptsType ] = useState([]);
  const [ selectedOptsFreq, setSelectedOptsFreq ] = useState([]);
  const [ selectedOptsStatus, setSelectedOptsStatus ] = useState([]);

  const handleTabSwitch = (newTab: string) => {
    switch (newTab) {
    case 'All':
      setAutoTab('All');
      window.sessionStorage.setItem('autoTab', 'All');
      break;
    case 'Shared with me':
      setAutoTab('Shared with me');
      window.sessionStorage.setItem('autoTab', 'Shared with me');
      break;
    case 'Recents':
      setAutoTab('Recents');
      window.sessionStorage.setItem('autoTab', 'Recents');
      break;
    case 'Starred':
      setAutoTab('Starred');
      window.sessionStorage.setItem('autoTab', 'Starred');
      break;
    default:
      setAutoTab('All');
    }
  };

  const checkForMonitor = () => {
    if (
      cur_user.selected_team?.attributes?.tier?.includes('enterprise') ||
      cur_user.selected_team?.attributes?.tier?.includes('standard') ||
      cur_user.selected_team?.attributes?.tier?.includes('pro')
    ) {
      setUserHasMonitor(true);
    }
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setMatchedTeamWithStripe(cus.id);
          cus.subscriptions.data.map((sub: any) => {
            if (
              sub.metadata.moduleName === 'BITE Standard' ||
              sub.metadata.moduleName === 'BITE Pro' ||
              sub.metadata.moduleName === 'BITE Enterprise'
            ) {
              setActiveSub(true);
            }
          });
        }
      });
    }
  };

  const getCusFromEmail = async() => {
    const res = await PaymentsAPI.getCustomerByEmail(
      cur_user.selected_team.attributes.team_admin[0]
    );
    if (res?.success) {
      res?.result?.data?.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setScreeningCus({ isLoading: false, data: cus });
        }
      });
    }
  };

  useEffect(() => {
    getCusFromEmail();
    checkForMonitor();
  }, [ activeSub ]);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const getCasesFromAPI = async(query: any, page: number) => {
    const mongoReq = {
      query: {
        ...query,
        groupId: cur_user.selected_team?.id,
        ...(selectedOptsType.length > 0 && { docType: selectedOptsType }),
        ...(selectedOptsFreq.length > 0 && { frequency: selectedOptsFreq }),
        ...(selectedOptsStatus.length > 0 && { status: selectedOptsStatus }),
        $or: [
          { sharedWith: cur_user.basics?.id },
          { sharedWith: cur_user.basics?.email },
          { createdBy: cur_user.basics?.id },
        ],
      },
      skip: cardTab === 'List' ? (page - 1) * 10 : (page - 1) * 9,
      limit: cardTab === 'List' ? 10 : 9,
      projection: {
        _id: 1,
        ticketName: 1,
        curNew: 1,
        curTotal: 1,
        status: 1,
        createdAt: 1,
        favorite: 1,
        docType: 1,
        sharedWith: 1,
        lastUpdate: 1
      },
    };
    getCaseCountFromAPI(mongoReq);
    const resp = await CasesAPI.searchCases(mongoReq);
    if (resp?.success) {
      setCases({ data: resp?.result, isLoaded: true, error: undefined });
    } else if (resp) {
      const error_msg = `Error querying cases: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setCases({ data: [], isLoaded: true, error: error_msg });
    }
  };

  const getSharedCasesFromAPI = async(query: any, page: number) => {
    const mongoReq = {
      query: {
        ...query,
        groupId: cur_user.selected_team?.id,
        ...(selectedOptsType.length > 0 && { docType: selectedOptsType }),
        ...(selectedOptsFreq.length > 0 && { frequency: selectedOptsFreq }),
        ...(selectedOptsStatus.length > 0 && { status: selectedOptsStatus }),
        $or: [
          { sharedWith: cur_user.basics?.id },
          { sharedWith: cur_user.basics?.email },
        ],
      },
      skip: cardTab === 'List' ? (page - 1) * 10 : (page - 1) * 9,
      limit: cardTab === 'List' ? 10 : 9,
      projection: {
        _id: 1,
        ticketName: 1,
        curNew: 1,
        curTotal: 1,
        status: 1,
        createdAt: 1,
        favorite: 1,
        docType: 1,
        sharedWith: 1,
        lastUpdate: 1
      },
    };
    getSharedCaseCountFromAPI(mongoReq);
    const resp = await CasesAPI.searchCases(mongoReq);
    if (resp?.success) {
      setSharedCases({ data: resp?.result, isLoaded: true, error: undefined });
    } else if (resp) {
      const error_msg = `Error querying shared cases: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setSharedCases({ data: [], isLoaded: true, error: error_msg });
    }
  };

  const getFavCasesFromAPI = async(query: any, page: number) => {
    const mongoReq = {
      query: {
        ...query,
        groupId: cur_user.selected_team?.id,
        favorite: true,
        ...(selectedOptsType.length > 0 && { docType: selectedOptsType }),
        ...(selectedOptsFreq.length > 0 && { frequency: selectedOptsFreq }),
        ...(selectedOptsStatus.length > 0 && { status: selectedOptsStatus }),
        $or: [
          { sharedWith: cur_user.basics?.id },
          { sharedWith: cur_user.basics?.email },
          { createdBy: cur_user.basics?.id },
        ],
      },
      skip: cardTab === 'List' ? (page - 1) * 10 : (page - 1) * 9,
      limit: cardTab === 'List' ? 10 : 9,
      projection: {
        _id: 1,
        ticketName: 1,
        curNew: 1,
        curTotal: 1,
        status: 1,
        createdAt: 1,
        favorite: 1,
        docType: 1,
        sharedWith: 1,
        lastUpdate: 1
      },
    };
    getFavCaseCountFromAPI(mongoReq);
    const resp = await CasesAPI.searchCases(mongoReq);
    if (resp?.success) {
      setFavCases({ data: resp?.result, isLoaded: true, error: undefined });
    } else if (resp) {
      const error_msg = `Error querying favorite cases: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
      setFavCases({ data: [], isLoaded: true, error: error_msg });
    }
  };

  const getCaseCountFromAPI = async(query: any) => {
    const resp = await CasesAPI.countCases(query);
    if (resp?.success) {
      const totalCount = resp?.result;
      setCaseCount(totalCount);
    } else if (resp) {
      const error_msg = `Error getting total number of case results: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const getSharedCaseCountFromAPI = async(query: any) => {
    const resp = await CasesAPI.countCases(query);
    if (resp?.success) {
      const totalCount = resp?.result;
      setSharedCaseCount(totalCount);
    } else if (resp) {
      const error_msg = `Error getting total number of shared case results: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const getFavCaseCountFromAPI = async(query: any) => {
    const resp = await CasesAPI.countCases(query);
    if (resp?.success) {
      const totalCount = resp?.result;
      setFavCaseCount(totalCount);
    } else if (resp) {
      const error_msg = `Error getting total number of shared case results: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const handleAdvancedSearch = async(search: string) => {
    const new_query = buildCaseQuery(
      search,
      '',
      '',
      ''
    );
    setCaseQuery(new_query);
    if (autoTab === 'All' || autoTab === 'Recents') {
      getCasesFromAPI(new_query, pageNum);
    } else if (autoTab === 'Shared with me'){
      getSharedCasesFromAPI(new_query, pageSharedNum);
    } else if (autoTab === 'Starred'){
      getFavCasesFromAPI(caseQuery, pageFavNum);
    }
  };

  const handlePageChange = (e: ChangeEvent<unknown>, new_page: number) => {
    if (autoTab === 'All' || autoTab === 'Recents') {
      setPageNum(new_page);
    } else if (autoTab === 'Shared with me'){
      setPageSharedNum(new_page);
    } else if (autoTab === 'Starred'){
      setPageFavNum(new_page);
    }
  };


  const addShared = async(email: string) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `shared the ticket with ${email}`,
      timeStamp: Date.now()
    };
    const update: any = {
      _id: selectedCase._id,
      sharedWith: [ ...selectedCase.sharedWith, email ],
      activity: [ ...cur_case.data.activity, actObj ]
    };

    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      handleShareTicketNotification(cur_case.data.ticketName, cur_user, email, cur_case.data._id);
      enqueueSnackbar('Successfully shared your ticket', { variant: 'success' });
    } else {
      const error_msg = 'Error sharing your ticket';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const getOwner = async(id: string) => {
    const res = await UsersAPI.getUser(id);
    if (res?.success) {
      return res?.result.basics.email;
    }
    return '';
  };

  useEffect(() => {
    const fetchOwner = async() => {
      if (!selectedCase || !selectedCase._id) {
        return;
      } // Prevent running when empty

      CasesAPI.getCase(selectedCase._id)
        ?.then(async(res: any) => {
          if (res?.success) {
            if (
              res.result.owner?.email !== cur_user.basics?.email &&
          !res.result.sharedWith.includes(cur_user.basics?.email) &&
          !res.result.sharedWith.includes(cur_user.basics?.id) &&
          !(res.result.createdBy === cur_user.basics?.id)
            ) {
              history.push('/');
            }

            setCase({ isLoaded: true, error: undefined, data: res.result });

            if (res.result.owner) {
              setOwnerEmail(res.result.owner?.email);
            } else {
              const email = await getOwner(res.result.createdBy);
              setOwnerEmail(email);
            }
          } else {
            const err_text = `Error loading case: ${res?.result}`;
            enqueueSnackbar(err_text, { variant: 'error' });
          }
        });
    };

    fetchOwner();
  }, [ selectedCase ]);

  useEffect(() => {
    getCasesFromAPI({}, pageNum);
    getSharedCasesFromAPI({}, pageSharedNum);
    getFavCasesFromAPI({}, pageFavNum);
  }, [ trigger ]);

  useEffect(() => {
    if (autoTab === 'All' || autoTab === 'Recents') {
      getCasesFromAPI(caseQuery, pageNum);
    } else if (autoTab === 'Shared with me'){
      getSharedCasesFromAPI(caseQuery, pageSharedNum);
    } else if (autoTab === 'Starred'){
      getFavCasesFromAPI(caseQuery, pageFavNum);
    }
  }, [ pageNum, pageFavNum, pageSharedNum, autoTab, cardTab ]);

  useEffect(() => {
    if (autoTab === 'All' || autoTab === 'Recents') {
      getCasesFromAPI(caseQuery, pageNum);
    } else if (autoTab === 'Shared with me'){
      getSharedCasesFromAPI(caseQuery, pageSharedNum);
    } else if (autoTab === 'Starred'){
      getFavCasesFromAPI(caseQuery, pageFavNum);
    }
  }, [ selectedOptsType, selectedOptsFreq, selectedOptsStatus ]);


  const helpContent = (
    <p className='module-header-tooltip-text'>
      Quickly search for high risk people, companies and products as well as
      cyber threat indicators. Filter by Lens for more accuracy
    </p>
  );

  if (!cases.isLoaded) {
    return (
      <CustomModuleHeaderLayout title='Monitor' helpContent={helpContent}>
        <div className='spinner-spacer-table'>
          <div className='loader'>Loading...</div>
        </div>
      </CustomModuleHeaderLayout>
    );
  }
  if (cases.error) {
    return <ContentError error={cases.error} />;
  }


  return (
    <CustomModuleHeaderLayout title='Monitor' helpContent={helpContent}>
      {activeSub && !userHasMonitor ? (
        <div className='no-license-modal'>
          <Player
            autoplay
            loop
            src='https://lottie.host/3c23c1ad-7f71-4e3d-8f19-69154715dbdc/SdWWjCmU1J.json'
            style={{ width: '20%' }}
          ></Player>
          <Typography
            variant='h6'
            className='text-white'
            style={{
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            }}
          >
            You need an assigned license to access this module
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: '18px',
              fontWeight: 700,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              marginTop: '20px',
              color: '#BDBEC1',
            }}
          >
            Visit Account &gt; Users &gt; to assign an available license to your
            user or contact your team admin
          </Typography>
        </div>
      ) : !userHasMonitor ? (
        <SubscribeModal
          name='BITE Monitor'
          description='Keep track of all risk reports, investigative analysis and trend reports'
          price={25}
          curUser={cur_user}
          stripeTeam={matchedTeamWithStripe}
          activeSub={activeSub}
        />
      ) : (
        <></>
      )}
      <div className={`${!userHasMonitor ? 'os-iframe-blur' : ''}`}>

        <div className='screening-tabs-container'>
          <div className='screening-tabs-inner'>
            <div
              className={`screening-tab-btn${
                autoTab === 'All' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('All')}
            >
              <div
                className={`screening-tab-btn-text${
                  autoTab === 'All' ? '-selected' : ''
                }`}
              >
                {
                  autoTab === 'All' ? (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <path d='M2.25 7.92385C2.25 7.49308 2.25 7.2777 2.30552 7.07935C2.3547 6.90365 2.43552 6.73839 2.54402 6.5917C2.6665 6.4261 2.83652 6.29387 3.17654 6.0294L8.26327 2.07306C8.52677 1.86812 8.65851 1.76565 8.80399 1.72626C8.93235 1.69151 9.06765 1.69151 9.19601 1.72626C9.34149 1.76565 9.47324 1.86812 9.73673 2.07306L14.8235 6.02941C15.1635 6.29387 15.3335 6.4261 15.456 6.5917C15.5645 6.73839 15.6453 6.90365 15.6945 7.07935C15.75 7.2777 15.75 7.49308 15.75 7.92385V13.35C15.75 14.1901 15.75 14.6102 15.5865 14.931C15.4427 15.2133 15.2132 15.4427 14.931 15.5866C14.6101 15.75 14.1901 15.75 13.35 15.75H4.65C3.80992 15.75 3.38988 15.75 3.06901 15.5866C2.78677 15.4427 2.5573 15.2133 2.41349 14.931C2.25 14.6102 2.25 14.1901 2.25 13.35V7.92385Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  ) : (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <path d='M2.25 7.92385C2.25 7.49308 2.25 7.2777 2.30552 7.07935C2.3547 6.90365 2.43552 6.73839 2.54402 6.5917C2.6665 6.4261 2.83652 6.29387 3.17654 6.0294L8.26327 2.07306C8.52677 1.86812 8.65851 1.76565 8.80399 1.72626C8.93235 1.69151 9.06765 1.69151 9.19601 1.72626C9.34149 1.76565 9.47324 1.86812 9.73673 2.07306L14.8235 6.02941C15.1635 6.29387 15.3335 6.4261 15.456 6.5917C15.5645 6.73839 15.6453 6.90365 15.6945 7.07935C15.75 7.2777 15.75 7.49308 15.75 7.92385V13.35C15.75 14.1901 15.75 14.6102 15.5865 14.931C15.4427 15.2133 15.2132 15.4427 14.931 15.5866C14.6101 15.75 14.1901 15.75 13.35 15.75H4.65C3.80992 15.75 3.38988 15.75 3.06901 15.5866C2.78677 15.4427 2.5573 15.2133 2.41349 14.931C2.25 14.6102 2.25 14.1901 2.25 13.35V7.92385Z' stroke='#BDBEC1' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  )
                }
              All
              </div>
            </div>
            <div
              className={`screening-tab-btn${
                autoTab === 'Shared with me' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Shared with me')}
            >
              <div
                className={`screening-tab-btn-text${
                  autoTab === 'Shared with me' ? '-selected' : ''
                }`}
              >
                {
                  autoTab === 'Shared with me' ? (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <path d='M13.5001 11.8777C14.592 12.4262 15.5282 13.3065 16.2116 14.4072C16.3469 14.6252 16.4145 14.7342 16.4379 14.8851C16.4855 15.1918 16.2757 15.5689 15.9901 15.6903C15.8495 15.75 15.6914 15.75 15.3751 15.75M12.0001 8.64918C13.1114 8.09692 13.8751 6.95015 13.8751 5.625C13.8751 4.29985 13.1114 3.15308 12.0001 2.60082M10.5001 5.625C10.5001 7.48896 8.98908 9 7.12512 9C5.26116 9 3.75012 7.48896 3.75012 5.625C3.75012 3.76104 5.26116 2.25 7.12512 2.25C8.98908 2.25 10.5001 3.76104 10.5001 5.625ZM1.91954 14.2038C3.11527 12.4084 5.00215 11.25 7.12512 11.25C9.24809 11.25 11.135 12.4084 12.3307 14.2038C12.5926 14.5971 12.7236 14.7937 12.7085 15.0449C12.6968 15.2405 12.5686 15.48 12.4123 15.5982C12.2116 15.75 11.9355 15.75 11.3834 15.75H2.86689C2.31475 15.75 2.03868 15.75 1.83795 15.5982C1.68166 15.48 1.55344 15.2405 1.5417 15.0449C1.52662 14.7937 1.65759 14.5971 1.91954 14.2038Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  ) : (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <path d='M13.5001 11.8777C14.592 12.4262 15.5282 13.3065 16.2116 14.4072C16.3469 14.6252 16.4145 14.7342 16.4379 14.8851C16.4855 15.1918 16.2757 15.5689 15.9901 15.6903C15.8495 15.75 15.6914 15.75 15.3751 15.75M12.0001 8.64918C13.1114 8.09692 13.8751 6.95015 13.8751 5.625C13.8751 4.29985 13.1114 3.15308 12.0001 2.60082M10.5001 5.625C10.5001 7.48896 8.98908 9 7.12512 9C5.26116 9 3.75012 7.48896 3.75012 5.625C3.75012 3.76104 5.26116 2.25 7.12512 2.25C8.98908 2.25 10.5001 3.76104 10.5001 5.625ZM1.91954 14.2038C3.11527 12.4084 5.00215 11.25 7.12512 11.25C9.24809 11.25 11.135 12.4084 12.3307 14.2038C12.5926 14.5971 12.7236 14.7937 12.7085 15.0449C12.6968 15.2405 12.5686 15.48 12.4123 15.5982C12.2116 15.75 11.9355 15.75 11.3834 15.75H2.86689C2.31475 15.75 2.03868 15.75 1.83795 15.5982C1.68166 15.48 1.55344 15.2405 1.5417 15.0449C1.52662 14.7937 1.65759 14.5971 1.91954 14.2038Z' stroke='#BDBEC1' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  )
                }
              Shared with me
              </div>
            </div>
            <div
              className={`screening-tab-btn${
                autoTab === 'Recents' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Recents')}
            >
              <div
                className={`screening-tab-btn-text${
                  autoTab === 'Recents' ? '-selected' : ''
                }`}
              >
                {
                  autoTab === 'Recents' ? (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <g clipPath='url(#clip0_26019_14127)'>
                        <path d='M9 4.5V9L12 10.5M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                      </g>
                      <defs>
                        <clipPath id='clip0_26019_14127'>
                          <rect width='18' height='18' fill='white'/>
                        </clipPath>
                      </defs>
                    </svg>
                  ) : (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <g clipPath='url(#clip0_26019_14127)'>
                        <path d='M9 4.5V9L12 10.5M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z' stroke='#BDBEC1' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                      </g>
                      <defs>
                        <clipPath id='clip0_26019_14127'>
                          <rect width='18' height='18' fill='white'/>
                        </clipPath>
                      </defs>
                    </svg>
                  )
                }
              Recents
              </div>
            </div>
            <div
              className={`screening-tab-btn${
                autoTab === 'Starred' ? '-selected' : ''
              }`}
              onClick={() => handleTabSwitch('Starred')}
            >
              <div
                className={`screening-tab-btn-text${
                  autoTab === 'Starred' ? '-selected' : ''
                }`}
              >
                {
                  autoTab === 'Starred' ? (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <path d='M8.46199 2.58993C8.63485 2.23972 8.72128 2.06462 8.83862 2.00867C8.94071 1.96 9.05931 1.96 9.1614 2.00867C9.27874 2.06462 9.36517 2.23972 9.53804 2.58993L11.178 5.9124C11.2291 6.01579 11.2546 6.06748 11.2919 6.10762C11.3249 6.14316 11.3645 6.17195 11.4085 6.1924C11.4582 6.21551 11.5152 6.22384 11.6293 6.24052L15.2977 6.77672C15.684 6.83318 15.8772 6.86141 15.9666 6.95577C16.0444 7.03786 16.0809 7.15066 16.0661 7.26277C16.0491 7.39162 15.9093 7.52782 15.6296 7.80022L12.9761 10.3848C12.8934 10.4653 12.852 10.5056 12.8253 10.5535C12.8017 10.596 12.7865 10.6426 12.7807 10.6908C12.7741 10.7453 12.7838 10.8022 12.8034 10.916L13.4295 14.5665C13.4955 14.9516 13.5285 15.1441 13.4665 15.2583C13.4125 15.3577 13.3165 15.4274 13.2053 15.448C13.0775 15.4717 12.9046 15.3808 12.5588 15.199L9.27928 13.4743C9.1771 13.4206 9.12601 13.3937 9.07218 13.3832C9.02452 13.3738 8.9755 13.3738 8.92784 13.3832C8.87402 13.3937 8.82293 13.4206 8.72074 13.4743L5.44119 15.199C5.09544 15.3808 4.92256 15.4717 4.79473 15.448C4.68351 15.4274 4.58754 15.3577 4.53355 15.2583C4.4715 15.1441 4.50452 14.9516 4.57056 14.5665L5.19666 10.916C5.21618 10.8022 5.22594 10.7453 5.21934 10.6908C5.21349 10.6426 5.19833 10.596 5.1747 10.5535C5.14802 10.5056 5.10666 10.4653 5.02394 10.3848L2.37042 7.80022C2.09075 7.52782 1.95091 7.39162 1.93389 7.26277C1.91909 7.15066 1.95567 7.03786 2.03344 6.95577C2.12283 6.86141 2.31598 6.83318 2.70228 6.77672L6.37073 6.24052C6.48482 6.22384 6.54186 6.21551 6.59154 6.1924C6.63552 6.17195 6.67512 6.14316 6.70814 6.10762C6.74543 6.06748 6.77095 6.01579 6.82198 5.9124L8.46199 2.58993Z' stroke='#4895EF' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  ) : (
                    <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' style={{ marginRight: '5px' }}>
                      <path d='M8.46199 2.58993C8.63485 2.23972 8.72128 2.06462 8.83862 2.00867C8.94071 1.96 9.05931 1.96 9.1614 2.00867C9.27874 2.06462 9.36517 2.23972 9.53804 2.58993L11.178 5.9124C11.2291 6.01579 11.2546 6.06748 11.2919 6.10762C11.3249 6.14316 11.3645 6.17195 11.4085 6.1924C11.4582 6.21551 11.5152 6.22384 11.6293 6.24052L15.2977 6.77672C15.684 6.83318 15.8772 6.86141 15.9666 6.95577C16.0444 7.03786 16.0809 7.15066 16.0661 7.26277C16.0491 7.39162 15.9093 7.52782 15.6296 7.80022L12.9761 10.3848C12.8934 10.4653 12.852 10.5056 12.8253 10.5535C12.8017 10.596 12.7865 10.6426 12.7807 10.6908C12.7741 10.7453 12.7838 10.8022 12.8034 10.916L13.4295 14.5665C13.4955 14.9516 13.5285 15.1441 13.4665 15.2583C13.4125 15.3577 13.3165 15.4274 13.2053 15.448C13.0775 15.4717 12.9046 15.3808 12.5588 15.199L9.27928 13.4743C9.1771 13.4206 9.12601 13.3937 9.07218 13.3832C9.02452 13.3738 8.9755 13.3738 8.92784 13.3832C8.87402 13.3937 8.82293 13.4206 8.72074 13.4743L5.44119 15.199C5.09544 15.3808 4.92256 15.4717 4.79473 15.448C4.68351 15.4274 4.58754 15.3577 4.53355 15.2583C4.4715 15.1441 4.50452 14.9516 4.57056 14.5665L5.19666 10.916C5.21618 10.8022 5.22594 10.7453 5.21934 10.6908C5.21349 10.6426 5.19833 10.596 5.1747 10.5535C5.14802 10.5056 5.10666 10.4653 5.02394 10.3848L2.37042 7.80022C2.09075 7.52782 1.95091 7.39162 1.93389 7.26277C1.91909 7.15066 1.95567 7.03786 2.03344 6.95577C2.12283 6.86141 2.31598 6.83318 2.70228 6.77672L6.37073 6.24052C6.48482 6.22384 6.54186 6.21551 6.59154 6.1924C6.63552 6.17195 6.67512 6.14316 6.70814 6.10762C6.74543 6.06748 6.77095 6.01579 6.82198 5.9124L8.46199 2.58993Z' stroke='#BDBEC1' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                    </svg>
                  )
                }
              Starred
              </div>
            </div>
          </div>
        </div>
        <>
          <SearchFilterComponent cardTab={cardTab} setCardTab={setCardTab} handleAdvancedSearch={handleAdvancedSearch} userHasMonitor={userHasMonitor} selectedOptsType={selectedOptsType} setSelectedOptsType={setSelectedOptsType} selectedOptsFreq={selectedOptsFreq} setSelectedOptsFreq={setSelectedOptsFreq} selectedOptsStatus={selectedOptsStatus} setSelectedOptsStatus={setSelectedOptsStatus}/>
          {
            cardTab === 'List' && (
              <MonitorTicketTable
                handlePageChange={handlePageChange}
                cases={autoTab === 'All' ? cases.data : autoTab === 'Shared with me' ? sharedCases.data : autoTab === 'Starred' ? favCases.data : cases.data}
                pageNum={autoTab === 'All' ? pageNum : autoTab === 'Shared with me' ? pageSharedNum : autoTab === 'Starred' ? pageFavNum : pageNum}
                setCases={autoTab === 'All' ? setCases : autoTab === 'Shared with me' ? setSharedCases : autoTab === 'Starred' ? setFavCases : setCases}
                total={autoTab === 'All' ? caseCount : autoTab === 'Shared with me' ? sharedCaseCount : autoTab === 'Starred' ? favCaseCount : caseCount}
                setSearchVal={setSearchVal}
                trigger={trigger}
                setTrigger={setTrigger}
                setSelectedCase={setSelectedCase}
                setShareModal={setShareModal}
                userHasMonitor={userHasMonitor}
              />
            )
          }
          {
            cardTab === 'Card' && (
              <AuditCardDash
                handlePageChange={handlePageChange}
                cases={autoTab === 'All' ? cases.data : autoTab === 'Shared with me' ? sharedCases.data : autoTab === 'Starred' ? favCases.data : cases.data}
                pageNum={autoTab === 'All' ? pageNum : autoTab === 'Shared with me' ? pageSharedNum : autoTab === 'Starred' ? pageFavNum : pageNum}
                setCases={autoTab === 'All' ? setCases : autoTab === 'Shared with me' ? setSharedCases : autoTab === 'Starred' ? setFavCases : setCases}
                total={autoTab === 'All' ? caseCount : autoTab === 'Shared with me' ? sharedCaseCount : autoTab === 'Starred' ? favCaseCount : caseCount}
                setSearchVal={setSearchVal}
                trigger={trigger}
                setTrigger={setTrigger}
                setSelectedCase={setSelectedCase}
                setShareModal={setShareModal}
                userHasMonitor={userHasMonitor}
              />
            )
          }
        </>
        {Object.keys(selectedCase).length > 0 && <Modal
          open={shareModal}
          onClose={() => setShareModal(false)}
        >
          <AddPersonModal
            handleClose={() => setShareModal(false)}
            sharedArr={selectedCase.sharedWith}
            owner={ownerEmail}
            addShared={addShared}
          />
        </Modal>}
      </div>
    </CustomModuleHeaderLayout>
  );
};

export default Monitor;
